import { OrganizationIndustry } from '@graphql/enums';
import type { Organization, UpdateOrganizationInput } from '@store/entities/meOrg';
import { type FormikErrors, type FormikTouched, getIn } from 'formik';
import * as Yup from 'yup';

import { emailFieldSchema, phoneNumberFieldSchema } from './fieldValidation';

export const shouldShowValidationError = (
  error: string | undefined,
  options: { touched?: boolean; value?: unknown }
): boolean => {
  if (!error) {
    return false;
  }

  if (options.touched) {
    return true;
  }

  return (
    options.value !== undefined &&
    options.value !== null &&
    String(options.value).trim() !== ''
  );
};

export const getFormFieldError = (
  touched: FormikTouched<any>,
  errors: FormikErrors<any>,
  field: string,
  value?: unknown
): string => {
  const errorField = getIn(errors, field);

  if (!errorField || typeof errorField !== 'string') {
    return '';
  }

  if (!shouldShowValidationError(errorField, { touched: !!getIn(touched, field), value })) {
    return '';
  }

  return errorField;
};

export const hasFormikErrors = <T extends Record<string, any>>(
  errors: FormikErrors<T>,
  touched?: FormikTouched<T>
): boolean => {
  // If touched object is provided, only check touched fields
  if (touched) {
    return Object.keys(touched).some((key) => touched[key as keyof T] && errors[key as keyof T]);
  }

  // Otherwise check all fields
  return Object.keys(errors).some((key) => errors[key as keyof T] !== undefined);
};

/**
 * GraphQL error structure from API responses
 */
export interface GraphQLFieldError {
  code?: string | null;
  field?: string | null;
  message: string;
  __typename?: string;
}

/**
 * Result of mapping GraphQL errors to Formik errors
 */
export interface MappedFormErrors {
  fieldErrors: Record<string, string>;
  generalErrors: GraphQLFieldError[];
}

/**
 * Maps GraphQL API errors to Formik field errors
 */
export const mapGraphQLMutationErrorsToFormik = (
  errors?: GraphQLFieldError[] | null,
  fieldNameTransform?: (fieldName: string) => string
): MappedFormErrors => {
  const fieldErrors: Record<string, string> = {};
  const generalErrors: GraphQLFieldError[] = [];

  if (!errors || errors.length === 0) {
    return { fieldErrors, generalErrors };
  }

  // Default transformer: Convert PascalCase to camelCase (e.g., "Password" -> "password")
  const defaultTransform = (fieldName: string): string =>
    fieldName.charAt(0).toLowerCase() + fieldName.slice(1);

  const transform = fieldNameTransform ?? defaultTransform;

  errors.forEach((error) => {
    if (error.field) {
      const fieldName = transform(error.field);

      fieldErrors[fieldName] = error.message;
    } else {
      generalErrors.push(error);
    }
  });

  return { fieldErrors, generalErrors };
};

export const passwordSchema = Yup.string()
  .required('Password is required')
  .min(8, 'Password must be at least 8 characters')
  .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
  .matches(/[0-9]/, 'Password must contain at least one number')
  .matches(/[^a-zA-Z0-9]/, 'Password must contain at least one special character');

export const WEBSITE_REGEX = /^(https?:\/\/)?(www\.)?[\w.-]+\.\w{2,}(\/.*)?$/;

export const organizationSchema = Yup.object().shape({
  name: Yup.string().required('Business name is required'),
  email: emailFieldSchema({ requiredMessage: 'Business email is required' }),
  phoneNumber: phoneNumberFieldSchema({ required: false }),
  website: Yup.string().matches(WEBSITE_REGEX, { message: 'Invalid URL format', excludeEmptyString: true }),
  industry: Yup.string().nullable().required('Industry is required'),
  otherIndustry: Yup.string().when('industry', {
    is: 'OTHER',
    then: (schema) => schema.required('Other industry is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  licenseNumber: Yup.string().required('License number is required'),
  mccCode: Yup.string().matches(/^\d+$/, 'MCC Code must be digits').nullable(),
});

export const organizationInitialValues = (org: Organization | null): UpdateOrganizationInput => {
  return {
    name: org?.name ?? '',
    email: org?.email ?? '',
    phoneNumber: org?.phoneNumber ?? '',
    industry: org?.industry ?? OrganizationIndustry.Tourism,
    website: org?.website ?? '',
    otherIndustry: org?.otherIndustry ?? '',
    licenseNumber: org?.licenseNumber ?? '',
    mccCode: org?.mccCode ?? '',
  };
};
